"use client";

import type React from "react";
import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Mail,
  Phone,
  MapPin,
  Send,
  ArrowUpRight,
  MessageSquare,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { LiquidGlassCard } from "@/components/ui/liquid-glass-card";
import { usePortfolio } from "@/components/portfolio-provider";

const defaultContactInfo = [
  {
    icon: Mail,
    title: "Email",
    value: "sakibul.islam0808@gmail.com",
    link: "mailto:sakibul.islam0808@gmail.com",
    linkText: "Send an email",
  },
  {
    icon: Phone,
    title: "Phone",
    value: "+880 1540581443",
    link: "tel:+8801540581443",
    linkText: "Make a call",
  },
  {
    icon: MapPin,
    title: "Location",
    value: "Mirpur, Dhaka",
    subValue: "Bangladesh",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

export default function Contact() {
  const { data } = usePortfolio();
  const dbContact = data?.contactInfo;

  const displayContactInfo = dbContact
    ? [
        {
          icon: Mail,
          title: "Email",
          value: dbContact.email,
          link: `mailto:${dbContact.email}`,
          linkText: "Send an email",
        },
        {
          icon: Phone,
          title: "Phone",
          value: dbContact.phone,
          link: `tel:${dbContact.phone.replace(/\s+/g, "")}`,
          linkText: "Make a call",
        },
        {
          icon: MapPin,
          title: "Location",
          value: dbContact.location,
          subValue: dbContact.subLocation,
        },
      ]
    : defaultContactInfo;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      toast.success("Message sent successfully!");
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Contact form submission failed:", error);
      toast.error(
        "Unable to send message right now. Please try again later or email directly.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-shell relative overflow-x-clip py-10 sm:py-14 md:py-18 lg:py-20">
      {/* Background Liquid Ambient Light */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.12)_0%,transparent_70%)]" />
        <div className="absolute -right-32 bottom-1/4 h-96 w-96 rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.08)_0%,transparent_70%)]" />
      </div>

      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-10"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-1.5 backdrop-blur-xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">
              Get In Touch
            </span>
          </div>
          <h2 className="section-title mb-4 font-bold">
            Let&apos;s Work <span className="accent-gradient-text">Together</span>
          </h2>
          <p className="section-subtitle mx-auto max-w-2xl text-muted-foreground">
            Have an ambitious project, an engineering opening, or want to explore
            a collaboration? I&apos;d love to connect.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Left Column: Contact Cards */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="lg:col-span-2 space-y-4"
          >
            {displayContactInfo.map((info) => (
              <motion.div key={info.title} variants={itemVariants}>
                <LiquidGlassCard
                  className="p-5 sm:p-6 rounded-3xl"
                  spotlightColor="rgba(201, 168, 76, 0.2)"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/15 border border-primary/25 shadow-[inset_0_1px_1px_rgba(255,255,255,0.25)]">
                      <info.icon className="h-5 w-5 text-primary" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-foreground text-base mb-0.5">
                        {info.title}
                      </h3>
                      <p className="text-muted-foreground text-xs sm:text-sm truncate">
                        {info.value}
                      </p>
                      {info.subValue && (
                        <p className="text-muted-foreground text-xs mt-0.5">
                          {info.subValue}
                        </p>
                      )}
                      {info.link && (
                        <a
                          href={info.link}
                          className="inline-flex items-center gap-1 text-primary text-xs font-semibold hover:text-foreground mt-2 group/link transition-colors"
                        >
                          <span>{info.linkText}</span>
                          <ArrowUpRight className="h-3 w-3 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                        </a>
                      )}
                    </div>
                  </div>
                </LiquidGlassCard>
              </motion.div>
            ))}

            {/* Quick Response Card */}
            <motion.div variants={itemVariants}>
              <LiquidGlassCard className="p-6 rounded-3xl border-primary/25 bg-primary/5">
                <div className="flex items-center gap-3 mb-2.5">
                  <div className="p-2 rounded-xl bg-primary/20 text-primary">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <h3 className="font-bold text-sm text-foreground">
                    Quick Response Guarantee
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  I typically respond within 24 hours. For urgent project
                  inquiries, feel free to call directly or connect via WhatsApp.
                </p>
              </LiquidGlassCard>
            </motion.div>
          </motion.div>

          {/* Right Column: Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-3"
          >
            <div className="liquid-glass rounded-3xl p-6 sm:p-8 md:p-10 relative overflow-hidden">
              {/* Top Specular Rim */}
              <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />

              <div className="flex items-center gap-3 mb-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/15 border border-primary/25 text-primary shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]">
                  <MessageSquare className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">
                    Send a Message
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Fill out the form below and I&apos;ll get back to you promptly.
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <label
                      htmlFor="name"
                      className={`text-xs font-semibold uppercase tracking-wider transition-colors ${
                        focusedField === "name" ? "text-primary" : "text-muted-foreground"
                      }`}
                    >
                      Your Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("name")}
                      onBlur={() => setFocusedField(null)}
                      required
                      className="h-12 rounded-2xl border-white/10 bg-white/[0.04] backdrop-blur-xl text-foreground placeholder:text-muted-foreground/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] focus:border-primary/50 focus:ring-1 focus:ring-primary/40 transition-all"
                    />
                  </div>

                  <div className="space-y-2">
                    <label
                      htmlFor="email"
                      className={`text-xs font-semibold uppercase tracking-wider transition-colors ${
                        focusedField === "email" ? "text-primary" : "text-muted-foreground"
                      }`}
                    >
                      Your Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => setFocusedField("email")}
                      onBlur={() => setFocusedField(null)}
                      required
                      className="h-12 rounded-2xl border-white/10 bg-white/[0.04] backdrop-blur-xl text-foreground placeholder:text-muted-foreground/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] focus:border-primary/50 focus:ring-1 focus:ring-primary/40 transition-all"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="subject"
                    className={`text-xs font-semibold uppercase tracking-wider transition-colors ${
                      focusedField === "subject" ? "text-primary" : "text-muted-foreground"
                    }`}
                  >
                    Subject
                  </label>
                  <Input
                    id="subject"
                    name="subject"
                    placeholder="Project Inquiry / Opportunity"
                    value={formData.subject}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("subject")}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="h-12 rounded-2xl border-white/10 bg-white/[0.04] backdrop-blur-xl text-foreground placeholder:text-muted-foreground/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] focus:border-primary/50 focus:ring-1 focus:ring-primary/40 transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="message"
                    className={`text-xs font-semibold uppercase tracking-wider transition-colors ${
                      focusedField === "message" ? "text-primary" : "text-muted-foreground"
                    }`}
                  >
                    Message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell me about your project, timeline, and how I can help..."
                    value={formData.message}
                    onChange={handleChange}
                    onFocus={() => setFocusedField("message")}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="min-h-[150px] rounded-2xl border-white/10 bg-white/[0.04] backdrop-blur-xl text-foreground placeholder:text-muted-foreground/60 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] focus:border-primary/50 focus:ring-1 focus:ring-primary/40 transition-all resize-none p-4"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="liquid-pill-btn w-full h-12 text-sm font-semibold uppercase tracking-wider text-black rounded-full shadow-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span>Sending Message...</span>
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <span>Send Message</span>
                      <Send className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
